import {
  type Action,
  FilterActionTypes,
  type Thunk,
  action,
  debug,
  thunk,
} from "easy-peasy";
import set from "lodash/set";

import type { ByDate, ByPeople, ByRestaurantId } from "./types";
import { Logger } from "../dependencies/logger";
import { batcher } from "../dependencies/utils";
import {
  type FirebaseModel,
  type FirebaseRegularTableSyncSlot,
  firebaseModel,
} from "../firebase/regularTableSync";
import type { NonEmptyArray } from "../types";
import { type LogEffect, createLogEffect } from "../utils/store";

const logEffect = (
  property: keyof FilterActionTypes<FirebaseRegularTableSyncModel>,
) =>
  createLogEffect<FirebaseRegularTableSyncModel>(
    "FIREBASE_REGULAR_TABLE_SYNC",
    property,
    false,
  );

const logger = new Logger(
  "RESTAURANT_STORE - FIREBASE_REGULAR_TABLE_SYNC",
  "LOG",
);
// logger.enable();

export type FirebaseRegularTableSyncById = ByRestaurantId<
  ByDate<ByPeople<FirebaseRegularTableSyncSlot | null>>
>;

export type FirebaseRegularTableSyncModel = {
  firebase: FirebaseModel;

  byId: FirebaseRegularTableSyncById;
  byIdOnChange: LogEffect<FirebaseRegularTableSyncModel>;
  clear: Action<FirebaseRegularTableSyncModel>;
  set: Action<
    FirebaseRegularTableSyncModel,
    {
      restaurantId: number;
      date: string;
      people: number;
      availability: FirebaseRegularTableSyncSlot | null;
    }[]
  >;
  unset: Action<
    FirebaseRegularTableSyncModel,
    { restaurantId: number; date: string }
  >;
  loading: boolean;
  loadingOnChange: LogEffect<FirebaseRegularTableSyncModel>;
  startLoading: Action<FirebaseRegularTableSyncModel>;
  stopLoading: Action<FirebaseRegularTableSyncModel, Event | void>;
  fetch: Thunk<
    FirebaseRegularTableSyncModel,
    {
      restaurantIds: number[];
      dates: NonEmptyArray<string>;
      people: number;
      onlyOnceById: ByRestaurantId<boolean>;
      abortSignal: AbortSignal;
    }
  >;
};

const BATCHER_PERIOD = 200;

const firebaseRegularTableSyncModel: FirebaseRegularTableSyncModel = {
  firebase: firebaseModel,

  byId: {},
  byIdOnChange: logEffect("byId"),
  clear: action((state) => {
    if (Object.keys(state.byId).length === 0) {
      return;
    }
    logger.log("Clear");
    state.byId = {};
  }),

  set: action((state, payload) => {
    logger.log("Set", payload);

    payload.forEach(({ restaurantId, date, people, availability }) => {
      const currentAvailability = state.byId[restaurantId]?.[date]?.[people];
      const nonProxiedCurrentAvailability = currentAvailability
        ? debug(currentAvailability)
        : currentAvailability;

      const hasChanged =
        JSON.stringify(nonProxiedCurrentAvailability) !==
        JSON.stringify(availability);

      if (hasChanged) {
        set(state.byId, [restaurantId, date, people], availability);
      }
    });
  }),
  unset: action((state, payload) => {
    logger.log("Unset", payload);

    delete state.byId[payload.restaurantId]?.[payload.date];
  }),

  loading: false,
  loadingOnChange: logEffect("loading"),
  startLoading: action((state) => {
    logger.log("Start loading");

    state.loading = true;
  }),
  stopLoading: action((state) => {
    logger.log("Stop loading");

    state.loading = false;
  }),

  fetch: thunk(async (actions, payload) => {
    if (payload.abortSignal.aborted) {
      return;
    }

    const count = payload.restaurantIds.length;

    logger.log(`Fetching availability (${count})...`);

    payload.abortSignal.addEventListener("abort", actions.stopLoading);

    actions.startLoading();

    const addToBatch = batcher(actions.set, BATCHER_PERIOD);

    await actions.firebase.subscribe({
      restaurantIds: payload.restaurantIds,
      dates: payload.dates,
      people: payload.people,
      onlyOnceById: payload.onlyOnceById,
      onValue: addToBatch,
      abortSignal: payload.abortSignal,
    });

    payload.abortSignal.removeEventListener("abort", actions.stopLoading);

    if (payload.abortSignal.aborted) {
      logger.log(`Fetch availability aborted (${count})`);

      return;
    }

    actions.stopLoading();

    logger.log(`Fetch availability done (${count})`);
  }),
};

export default firebaseRegularTableSyncModel;
